import { create } from 'zustand';
import type { MatNode, MediaType, PromptItem, ValueUpdater } from './seedanceTypes';
import { resolveValueUpdater } from './seedanceStoreUtils';
import {
  loadSeedanceWorkspaceIndex,
  saveSeedanceWorkspaceIndex,
} from '../utils/seedanceStorage';

/**
 * Seedance 创作工作台的核心业务状态。
 *
 * 这些状态虽然主要在「创作工作台」编辑，但会被素材库“使用素材”、
 * 生成任务、IndexedDB 持久化和任务上下文快照共同读写，所以先提升为公共状态。
 *
 * 不要把拖拽中的节点 id、提示词输入框光标、hover 缩略图等纯交互状态放进来；
 * 它们属于具体页面/组件内部状态。
 */
interface SeedanceWorkspaceState {
  /** 创作工作台中的参考素材节点。 */
  nodes: MatNode[];
  /** 多段提示词输入。 */
  prompts: PromptItem[];
  /** 生成时长，单位秒。 */
  duration: number;
  /** 生成画幅比例，例如 16:9、9:16。 */
  ratio: string;
  /** 生成分辨率，例如 720p。 */
  resolution: string;
  /** 当前选择的视频生成模型。 */
  model: string;
  /** 是否开启字幕（默认关闭；关闭时在提交提示词前加「严禁出现字幕」）。 */
  subtitleEnabled: boolean;
  setNodes: (nodes: ValueUpdater<MatNode[]>) => void;
  setPrompts: (prompts: ValueUpdater<PromptItem[]>) => void;
  setDuration: (duration: number) => void;
  setRatio: (ratio: string) => void;
  setResolution: (resolution: string) => void;
  setModel: (model: string) => void;
  setSubtitleEnabled: (subtitleEnabled: boolean) => void;
  /** 用于生成节点 ID 的自增序列。 */
  nodeSeq: number;
  setNodeSeq: (seq: number) => void;
  /** 追加一个素材节点。 */
  addMatNode: (type: MediaType, opts?: Partial<MatNode>) => MatNode;
  /** 将素材库资产应用到工作台节点（优先持久化素材 id，URL 仅为当前可用的预览快照）。 */
  applyLibAssetToNode: (nodeId: string, asset: {
    url: string | null;
    thumb?: string | null;
    name: string;
    fileSize?: number;
    /** 与列表里的 `Asset.id` 一致时也会写入节点的 sourceAssetId */
    sourceAssetId?: string;
    id?: string;
  }) => void;
  /** 提示词中 @图x 悬浮预览状态。 */
  mentionThumbHover: {
    thumbUrl: string | null;
    label: string;
    x: number;
    y: number;
  } | null;
  setMentionThumbHover: (hover: any) => void;
}

/** 页面和工作区恢复时共用的默认模型，避免默认值散落在组件里。 */
export const DEFAULT_SEEDANCE_MODEL = 'doubao-seedance-2-0-260128';

const initialWorkspaceIndex = loadSeedanceWorkspaceIndex();
const initialWorkspaceNodes =
  initialWorkspaceIndex?.nodes.map((node) => ({
    ...node,
    url: '',
    thumb: '',
  })) ?? [];

export const useSeedanceWorkspaceStore = create<SeedanceWorkspaceState>((set) => ({
  nodes: initialWorkspaceNodes,
  prompts: initialWorkspaceIndex?.prompts.length
    ? initialWorkspaceIndex.prompts
    : [{ id: 0, text: '' }],
  duration: initialWorkspaceIndex?.params.duration || 10,
  ratio: initialWorkspaceIndex?.params.ratio || '16:9',
  resolution: initialWorkspaceIndex?.params.resolution || '720p',
  model: initialWorkspaceIndex?.params.model || DEFAULT_SEEDANCE_MODEL,
  subtitleEnabled: Boolean(initialWorkspaceIndex?.params.subtitleEnabled),
  setNodes: (nodes) =>
    set((state) => ({ nodes: resolveValueUpdater(state.nodes, nodes) })),
  setPrompts: (prompts) =>
    set((state) => ({ prompts: resolveValueUpdater(state.prompts, prompts) })),
  setDuration: (duration) => set({ duration }),
  setRatio: (ratio) => set({ ratio }),
  setResolution: (resolution) => set({ resolution }),
  setModel: (model) => set({ model }),
  setSubtitleEnabled: (subtitleEnabled) => set({ subtitleEnabled }),
  nodeSeq: initialWorkspaceIndex?.nodeSeq || 1,
  setNodeSeq: (nodeSeq) => set({ nodeSeq }),
  addMatNode: (type, opts = {}) => {
    const { nodeSeq, setNodes, setNodeSeq } = useSeedanceWorkspaceStore.getState();
    const seq = nodeSeq;
    const id = 'mn_' + seq;
    const defaultName = (type === 'image' ? '图片' : type === 'video' ? '视频' : '音频') + (seq + 1);
    const node: MatNode = {
      id,
      type,
      name: opts.name || defaultName,
      url: opts.url || '',
      thumb: opts.thumb || '',
      connected: false,
      fileSize: opts.fileSize,
      fileType: opts.fileType,
      duration: opts.duration,
      sourceAssetId: opts.sourceAssetId,
    };
    setNodes((prev) => [...prev, node]);
    setNodeSeq(seq + 1);
    return node;
  },
  applyLibAssetToNode: (nodeId, asset) => {
    const { setNodes } = useSeedanceWorkspaceStore.getState();
    const sid =
      (asset.sourceAssetId && String(asset.sourceAssetId).trim()) ||
      (asset.id !== undefined && String(asset.id).trim()) ||
      undefined;
    setNodes((prev) =>
      prev.map((n) =>
        n.id === nodeId
          ? {
              ...n,
              url: asset.url || '',
              thumb: asset.thumb || asset.url || '',
              name: asset.name,
              fileSize: asset.fileSize,
              ...(sid ? { sourceAssetId: sid } : {}),
            }
          : n,
      ),
    );
  },
  mentionThumbHover: null,
  setMentionThumbHover: (mentionThumbHover) => set({ mentionThumbHover }),
}));

/** 将工作台业务索引同步写入 localStorage；预览 URL 不属于持久化数据。 */
export const persistSeedanceWorkspaceState = (options: { allowEmpty?: boolean } = {}) => {
  const {
    nodes,
    prompts,
    nodeSeq,
    ratio,
    resolution,
    duration,
    model,
    subtitleEnabled,
  } = useSeedanceWorkspaceStore.getState();
  const existing = loadSeedanceWorkspaceIndex();
  if (!options.allowEmpty && nodes.length === 0 && (existing?.nodes.length ?? 0) > 0) {
    return false;
  }
  const promptSeq = prompts.reduce((max, p) => Math.max(max, p.id + 1), 1);
  return saveSeedanceWorkspaceIndex({
    version: 1,
    nodes: nodes.map(({ url: _url, thumb: _thumb, ...node }) => node),
    prompts,
    promptSeq,
    nodeSeq,
    params: { ratio, resolution, duration, model, subtitleEnabled },
  });
};
